import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import TicketCard from '../components/TicketCard';
import DebugPanel from '../components/DebugPanel';
import firebaseService from '../services/firebaseService';
import footballDataService from '../services/footballDataService';
import ticketUpdateService from '../services/ticketUpdateService';
import './Live.css';

// Simple component for displaying real match data
const RealMatchCard = ({ match, index }) => {
  const getStatusText = (status) => {
    switch (status) {
      case 'LIVE':
      case 'IN_PLAY':
        return 'LIVE';
      case 'FINISHED':
        return 'დასრულებული';
      case 'SCHEDULED':
        return 'დაგეგმილი';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'LIVE':
      case 'IN_PLAY':
        return '#ff4444';
      case 'FINISHED':
        return '#666';
      case 'SCHEDULED':
        return '#4CAF50';
      default:
        return '#666';
    }
  };

  return (
    <motion.div
      className="real-match-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -2 }}
    >
      <div className="match-header">
        <div className="competition-name">
          {match.competition?.name || 'უცნობი'}
        </div>
        <div
          className="match-status"
          style={{ color: getStatusColor(match.status) }}
        >
          {getStatusText(match.status)}
        </div>
      </div>

      <div className="teams-section">
        <div className="team home-team">
          <span className="team-name">{match.homeTeam?.name || 'უცნობი'}</span>
        </div>
        <div className="vs-section">
          {match.status === 'FINISHED' && match.score?.fullTime ? (
            <div className="score">
              {match.score.fullTime.home ?? 0} - {match.score.fullTime.away ?? 0}
            </div>
          ) : (
            <span className="vs-text">VS</span>
          )}
        </div>
        <div className="team away-team">
          <span className="team-name">{match.awayTeam?.name || 'უცნობი'}</span>
        </div>
      </div>

      <div className="match-info">
        {/* Time hidden as per request */}
        {match.matchday && (
          <div className="match-day">
            მატჩი #{match.matchday}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Live = () => {
  const [liveMatches, setLiveMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [realLiveMatches, setRealLiveMatches] = useState([]);
  const [realUpcomingMatches, setRealUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [updatingCards, setUpdatingCards] = useState(new Set());
  const [apiConnected, setApiConnected] = useState(false);



  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    // Helper function to check if a match is currently live based on Tbilisi time
    const isMatchLive = (match) => {
      if (match.status === 'live') return true;

      // Check if match should be live based on time (Tbilisi timezone: UTC+4)
      if (match.date && match.matchTime) {
        try {
          const now = new Date();
          const tbilisiOffset = 4 * 60; // Tbilisi is UTC+4
          const localOffset = now.getTimezoneOffset();
          const tbilisiTime = new Date(now.getTime() + (tbilisiOffset + localOffset) * 60000);

          const matchDateTime = new Date(`${match.date}T${match.matchTime}:00`);
          const matchEndTime = new Date(matchDateTime.getTime() + 120 * 60000); // Match duration ~120 mins

          return tbilisiTime >= matchDateTime && tbilisiTime <= matchEndTime;
        } catch (error) {
          console.error('Error checking match time:', error);
          return false;
        }
      }
      return false;
    };

    const setupSubscription = async () => {
      try {
        console.log('📡 Live: Setting up real-time subscription...');

        // Set up real-time listener for automatic updates
        unsubscribe = await firebaseService.subscribeToMatches((updatedMatches) => {
          if (!isMounted) return;

          console.log('📡 Live: Real-time update received -', updatedMatches?.length || 0, 'matches');

          if (!Array.isArray(updatedMatches)) {
            console.error('❌ Live: Subscription callback received non-array:', updatedMatches);
            return;
          }

          // Filter only truly live matches based on status or time
          let newLive = updatedMatches.filter(match => isMatchLive(match));
          let newUpcoming = updatedMatches.filter(match =>
            match.status === 'pending' &&
            match.date &&
            match.matchTime &&
            !isMatchLive(match)
          );

          console.log('📊 Live: Real-time filtered - Live:', newLive.length, 'Upcoming:', newUpcoming.length);

          setLiveMatches(newLive);
          setUpcomingMatches(newUpcoming);
          setLastUpdate(new Date());

          // Track which cards are being updated (for animation)
          const updatingIds = new Set();
          if (updatingIds.size > 0) {
            setUpdatingCards(updatingIds);
            // Clear the updating state after animation
            setTimeout(() => {
              if (isMounted) setUpdatingCards(new Set());
            }, 1000);
          }
        });

        console.log('✅ Live: Real-time subscription established successfully');
      } catch (error) {
        console.error('❌ Live: Error setting up subscription:', error);
      }
    };

    const fetchRealData = async () => {
      try {
        // Check API connection
        await footballDataService.healthCheck();
        setApiConnected(true);

        // Fetch real live matches
        const liveData = await footballDataService.getLiveMatches();
        const upcomingData = await footballDataService.getUpcomingMatches();

        if (isMounted) {
          setRealLiveMatches(liveData);
          setRealUpcomingMatches(upcomingData);
        }
      } catch (error) {
        console.error('Error fetching real data:', error);
        setApiConnected(false);
      }
    };

    const fetchData = async () => {
      if (!isMounted) return;

      setLoading(true);

      try {
        console.log('🔄 Live: Starting to fetch matches from Firebase...');

        // Load ticket matches from Firebase
        const matches = await firebaseService.getAllMatches();
        console.log('📥 Live: getAllMatches returned', matches?.length || 0, 'matches');

        if (!isMounted) return;

        if (!Array.isArray(matches)) {
          console.error('❌ Live: getAllMatches did not return an array:', matches);
          setLiveMatches([]);
          setUpcomingMatches([]);
          return;
        }

        // Filter only truly live matches based on status or time
        let live = matches.filter(match => isMatchLive(match));
        let upcoming = matches.filter(match =>
          match.status === 'pending' &&
          match.date &&
          match.matchTime &&
          !isMatchLive(match)
        );

        console.log('📊 Live: Filtered - Live:', live.length, 'Upcoming:', upcoming.length, 'Total:', matches.length);

        console.log('✅ Live: Setting state - Live:', live.length, 'matches, Upcoming:', upcoming.length);
        setLiveMatches(live);
        setUpcomingMatches(upcoming);
      } catch (error) {
        console.error('❌ Live: Error loading matches:', error);
        if (!isMounted) return;
        setLiveMatches([]);
        setUpcomingMatches([]);
      }

      // Fetch real data
      try {
        await fetchRealData();
      } catch (error) {
        console.error('❌ Live: Error fetching real data:', error);
      }

      if (isMounted) {
        setLoading(false);
        console.log('✅ Live: Initial load complete');
      }
    };

    fetchData();
    setupSubscription();

    // Set up ticket update service callback
    const updateCallback = (updatedTickets) => {
      if (!isMounted) return;
      console.log('Tickets updated:', updatedTickets);
      setLastUpdate(new Date());
    };

    ticketUpdateService.onUpdate(updateCallback);

    // Periodic refresh of real data every 2 minutes
    const realDataInterval = setInterval(fetchRealData, 2 * 60 * 1000);

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      }
      ticketUpdateService.offUpdate(updateCallback);
      clearInterval(realDataInterval);
    };
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('ka-GE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };



  if (loading) {
    return (
      <div className="live">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">იტვირთება ლაივ მონაცემები...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="live"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <motion.h1
            className="page-title"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            LIVE 🔴
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            მატჩები რეალურ დროში
          </motion.p>
        </div>

        {/* Live Status */}
        <motion.div
          className="live-status"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="status-indicator">
            <div className="live-dot"></div>
            <span className="status-text">ლაივ რეჟიმი</span>
            {apiConnected && <span className="api-status">🟢 API</span>}
          </div>
          <div className="last-update">
            ბოლო განახლება: {formatTime(lastUpdate)}
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          className="tabs-container"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'live' ? 'active' : ''}`}
              onClick={() => setActiveTab('live')}
            >
              <span className="tab-icon">🎫</span>
              <span className="tab-text">ბილეთები ({liveMatches.length})</span>
            </button>
            <button
              className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
              onClick={() => setActiveTab('upcoming')}
            >
              <span className="tab-icon">⏰</span>
              <span className="tab-text">მოახლოებული ({upcomingMatches.length})</span>
            </button>
            {apiConnected && (
              <>
                <button
                  className={`tab ${activeTab === 'real-live' ? 'active' : ''}`}
                  onClick={() => setActiveTab('real-live')}
                >
                  <span className="tab-icon">🔴</span>
                  <span className="tab-text">რეალური ლაივი ({realLiveMatches.length})</span>
                </button>
                <button
                  className={`tab ${activeTab === 'real-upcoming' ? 'active' : ''}`}
                  onClick={() => setActiveTab('real-upcoming')}
                >
                  <span className="tab-icon">📅</span>
                  <span className="tab-text">რეალური მოახლოებული ({realUpcomingMatches.length})</span>
                </button>
              </>
            )}
          </div>
        </motion.div>

        {/* Content */}
        <div className="tab-content">
          {activeTab === 'live' && (
            <motion.div
              className="live-matches"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {liveMatches.length > 0 ? (
                <div className="matches-grid">
                  <div className="tc-header">
                    <span className="tc-header-label">კლუბები</span>
                    <span className="tc-header-label">პროგნოზი</span>
                    <span className="tc-header-label">კოეფ. | თარიღი</span>
                  </div>
                  {liveMatches.map((match, index) => (
                    <TicketCard
                      key={match.id}
                      ticket={match}
                      isLive={true}
                      index={index}
                      isUpdating={updatingCards.has(match.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-container">
                  <div className="empty-icon">🎫</div>
                  <h3 className="empty-title">ლაივ ბილეთები არ არის</h3>
                  <p className="empty-message">
                    ამჟამად ლაივ ბილეთები არ არის.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'upcoming' && (
            <motion.div
              className="upcoming-matches"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {upcomingMatches.length > 0 ? (
                <div className="matches-grid">
                  <div className="tc-header">
                    <span className="tc-header-label">კლუბები</span>
                    <span className="tc-header-label">პროგნოზი</span>
                    <span className="tc-header-label">კოეფ. | თარიღი</span>
                  </div>
                  {upcomingMatches.map((match, index) => (
                    <TicketCard
                      key={match.id}
                      ticket={match}
                      index={index}
                      isUpdating={updatingCards.has(match.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-container">
                  <div className="empty-icon">⏰</div>
                  <h3 className="empty-title">მოახლოებული ბილეთები არ არის</h3>
                  <p className="empty-message">
                    ახლო მომავალში ბილეთები არ არის დაგეგმილი.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'real-live' && apiConnected && (
            <motion.div
              className="real-live-matches"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {realLiveMatches.length > 0 ? (
                <div className="matches-grid">
                  {realLiveMatches.map((match, index) => (
                    <RealMatchCard
                      key={match.id}
                      match={match}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-container">
                  <div className="empty-icon">🔴</div>
                  <h3 className="empty-title">რეალური ლაივ მატჩები არ არის</h3>
                  <p className="empty-message">
                    ამჟამად რეალური ლაივ მატჩები არ მიმდინარეობს.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'real-upcoming' && apiConnected && (
            <motion.div
              className="real-upcoming-matches"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {realUpcomingMatches.length > 0 ? (
                <div className="matches-grid">
                  {realUpcomingMatches.map((match, index) => (
                    <RealMatchCard
                      key={match.id}
                      match={match}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-container">
                  <div className="empty-icon">📅</div>
                  <h3 className="empty-title">რეალური მოახლოებული მატჩები არ არის</h3>
                  <p className="empty-message">
                    ახლო მომავალში რეალური მატჩები არ არის დაგეგმილი.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {!apiConnected && (activeTab === 'real-live' || activeTab === 'real-upcoming') && (
            <div className="api-disconnected">
              <div className="empty-icon">🔌</div>
              <h3 className="empty-title">API კავშირი გაწყვეტილია</h3>
              <p className="empty-message">
                რეალური მონაცემების სანახავად საჭიროა API გასაღები.
                <br />
                <a href="https://www.football-data.org/client/register" target="_blank" rel="noopener noreferrer">
                  მიიღეთ API გასაღები
                </a>
              </p>
            </div>
          )}
        </div>

        {/* Debug Panel - only show in development or when explicitly enabled */}
        {(process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true')) && (
          <DebugPanel />
        )}

      </div>
    </motion.div>
  );
};

export default Live;